import { isPlatform } from '@ionic/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useFullscreen } from 'react-use';
import { logger } from '../../util/logger';

interface FullScreenModeResults {
  requestFullScreen: (el: HTMLElement) => Promise<void>;
  closeFullScreen: () => void;
  isFullScreen: boolean;
  isFullScreenSupported: boolean;
}

const useFullScreenMode = (): FullScreenModeResults => {
  const [isEnabled, setEnabled] = useState<boolean>(false);
  const elementRef = useRef<HTMLElement | null>(null);

  const isFullScreen = useFullscreen(elementRef, isEnabled, {
    onClose: () => {
      logger.info('closing full screen');
      setEnabled(false);
    },
  });

  const closeFullScreen = useCallback(() => {
    setEnabled(false);
  }, []);

  const requestFullScreen = useCallback(async (el: HTMLElement) => {
    logger.info('enabling full screen');
    elementRef.current = el;
    setEnabled(true);
    return;
  }, []);

  const results = useMemo<FullScreenModeResults>(
    () => ({
      isFullScreen,
      requestFullScreen,
      closeFullScreen,
      isFullScreenSupported: isPlatform('desktop'),
    }),
    [closeFullScreen, isFullScreen, requestFullScreen]
  );

  return results;
};

export { useFullScreenMode };
